// Slack Integration Component with Channel Selector

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Hash, Users, Lock, Send, Settings, RefreshCw, AlertCircle, CheckCircle, MessageCircle } from 'lucide-react';
import { slackApi, slackQueries, setupSlackTokens, type SlackChannel, type SlackMessage } from '../../services/api/slack.api';

interface SlackIntegrationProps {
  className?: string;
  defaultChannelId?: string;
  preFilledMessage?: string;
  onMessageSent?: (channelId: string, message: string) => void;
  onError?: (error: string) => void;
}

interface SlackConfig {
  botToken: string;
  userToken?: string;
  webhookUrl?: string;
}

export const SlackIntegration: React.FC<SlackIntegrationProps> = ({
  className = '',
  defaultChannelId,
  preFilledMessage,
  onMessageSent,
  onError,
}) => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState(defaultChannelId || '');
  const [message, setMessage] = useState(preFilledMessage || '');
  const [config, setConfig] = useState<SlackConfig>({ botToken: '' });
  const [showConfig, setShowConfig] = useState(false);
  const [messageHistory, setMessageHistory] = useState<Array<{
    id: string;
    channelId: string;
    channelName: string;
    text: string;
    timestamp: string;
    success: boolean;
    type: 'sent' | 'received';
    user?: string;
  }>>([]);
  const [currentMutationId, setCurrentMutationId] = useState<string | null>(null);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [lastMessageCount, setLastMessageCount] = useState(0);

  const queryClient = useQueryClient();

  // Check if tokens are configured
  useEffect(() => {
    // Only use localStorage for token storage - never bundle tokens in client
    // Environment variables with VITE_ prefix get bundled and expose secrets

    // Only check localStorage for saved configuration
    const savedConfig = localStorage.getItem('slack-config');
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        setConfig(parsed);
        if (parsed.botToken) {
          setupSlackTokens(parsed.botToken, parsed.userToken);
          setIsConfigured(true);
          console.log('Slack configured from localStorage');
        }
      } catch (error) {
        console.error('Failed to parse saved Slack config:', error);
      }
    }
  }, []);

  // Update message when preFilledMessage changes
  useEffect(() => {
    if (preFilledMessage) {
      setMessage(preFilledMessage);
    }
  }, [preFilledMessage]);

  // Load message history from localStorage
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem('slack-message-history');
      if (savedHistory) {
        setMessageHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error('Failed to load message history:', error);
    }
  }, []);

  // Save message history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('slack-message-history', JSON.stringify(messageHistory));
    } catch (error) {
      console.error('Failed to save message history:', error);
    }
  }, [messageHistory]);

  // Fetch channels
  const {
    data: channels = [],
    isLoading: channelsLoading,
    error: channelsError,
    refetch: refetchChannels,
  } = useQuery({
    ...slackQueries.channels(),
    enabled: isConfigured,
    onError: (error: any) => {
      onError?.(error.message || 'Failed to fetch channels');
    },
  });

  // Test connection
  const {
    data: connectionTest,
    isLoading: connectionLoading,
    error: connectionError,
  } = useQuery({
    ...slackQueries.connection(),
    enabled: isConfigured,
    retry: 1,
  });

  // Real-time webhook connection state
  const [webhookConnected, setWebhookConnected] = useState(false);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [usePolling, setUsePolling] = useState(false);

  // Setup webhook connection for real-time messages
  useEffect(() => {
    if (!isConfigured) return;

    const SLACK_PROXY_URL = 'http://localhost:3003';
    const sseUrl = `${SLACK_PROXY_URL}/api/slack/events/stream`;

    console.log('Connecting to Slack webhook stream:', sseUrl);
    const source = new EventSource(sseUrl);
    setEventSource(source);

    source.onopen = () => {
      console.log('✅ Webhook connection established');
      setWebhookConnected(true);
      setUsePolling(false); // Disable polling since webhooks work
    };

    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📨 Webhook event received:', data);

        if (data.type === 'connection') {
          console.log('🔗 Webhook connection confirmed');
          return;
        }

        if (data.type === 'heartbeat') {
          console.log('💓 Webhook heartbeat');
          return;
        }

        if (data.type === 'slack_message' && data.event) {
          const msg = data.event;
          console.log('💬 New Slack message via webhook:', msg);

          // Only process messages for the currently selected channel
          if (selectedChannelId && msg.channel === selectedChannelId) {
            const channelName = (channels as SlackChannel[]).find(c => c.id === selectedChannelId)?.name || selectedChannelId;

            const receivedMessage = {
              id: msg.ts,
              channelId: selectedChannelId,
              channelName,
              text: msg.text,
              timestamp: new Date(parseFloat(msg.ts) * 1000).toISOString(),
              success: true,
              type: 'received' as const,
              user: msg.user || 'Unknown User'
            };

            // Add message to history, avoiding duplicates
            setMessageHistory(prev => {
              const existingIds = new Set(prev.map(m => m.id));
              if (!existingIds.has(receivedMessage.id)) {
                console.log('✨ Adding new message from webhook:', receivedMessage.text);
                setNewMessageCount(prev => prev + 1);
                setTimeout(() => setNewMessageCount(prev => Math.max(0, prev - 1)), 3000);

                return [...prev, receivedMessage].sort(
                  (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                );
              }
              return prev;
            });
          }
        }
      } catch (error) {
        console.error('❌ Error parsing webhook event:', error);
      }
    };

    source.onerror = (error) => {
      console.error('❌ Webhook connection error, falling back to polling:', error);
      setWebhookConnected(false);
      setUsePolling(true); // Enable polling as fallback
    };

    return () => {
      console.log('🔌 Closing webhook connection');
      source.close();
      setWebhookConnected(false);
    };
  }, [isConfigured, selectedChannelId, channels]);

  // Fetch messages from selected channel (initial load + polling fallback)
  const {
    data: channelMessages,
    isLoading: messagesLoading,
    error: messagesError,
    refetch: refetchMessages,
  } = useQuery({
    ...slackQueries.channelMessages(selectedChannelId, 20),
    enabled: isConfigured && !!selectedChannelId,
    retry: false, // Don't retry on scope errors
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: true, // Refetch when component mounts or channel changes
    // Use polling as fallback if webhooks don't work
    refetchInterval: usePolling ? 5000 : false,
    refetchIntervalInBackground: usePolling,
  });

  // Merge received messages into message history
  useEffect(() => {
    if (channelMessages?.messages && selectedChannelId) {
      const channelName = (channels as SlackChannel[]).find(c => c.id === selectedChannelId)?.name || selectedChannelId;

      // Convert Slack messages to our format
      const receivedEntries = channelMessages.messages
        .filter(msg => msg.type === 'message' && msg.text) // Filter out system messages
        .map(msg => ({
          id: msg.ts,
          channelId: selectedChannelId,
          channelName,
          text: msg.text,
          timestamp: new Date(parseFloat(msg.ts) * 1000).toISOString(),
          success: true,
          type: 'received' as const,
          user: msg.user_profile?.real_name || msg.user_profile?.display_name || 'Unknown User'
        }));

      // Update message history with received messages, avoiding duplicates
      setMessageHistory(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const newMessages = receivedEntries.filter(msg => !existingIds.has(msg.id));

        if (newMessages.length > 0) {
          // Track new message count for notifications
          setNewMessageCount(newMessages.length);
          setTimeout(() => setNewMessageCount(0), 3000); // Clear after 3 seconds

          // Merge and sort by timestamp
          const merged = [...prev, ...newMessages].sort(
            (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
          return merged;
        }
        return prev;
      });
    }
  }, [channelMessages, selectedChannelId, channels, messagesError]);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async ({ channelId, text }: { channelId: string; text: string }) => {
      console.log('Attempting to send message to channel:', channelId);

      try {
        // First attempt - direct send
        return await slackApi.postMessage(channelId, text);
      } catch (error: any) {
        console.error('Send message error:', error);

        // If bot is not in channel, try to join it first
        if (error.errorCode === 'not_in_channel' ||
            error.message && (error.message.includes('not_in_channel') ||
                            error.message.includes('Bot is not in this channel') ||
                            error.message.includes('channel_not_found'))) {
          console.log('Bot not in channel, attempting to join...', { channelId, error: error.message });

          try {
            const joinResult = await slackApi.joinChannel(channelId);
            console.log('Join channel result:', joinResult);
            console.log('Successfully joined channel, retrying message...');
            // Retry the message send after successful join
            return await slackApi.postMessage(channelId, text);
          } catch (joinError: any) {
            console.error('Failed to join channel:', joinError);
            console.error('Join error details:', { errorCode: joinError.errorCode, message: joinError.message, response: joinError.response });

            if (joinError.errorCode === 'missing_scope') {
              const response = joinError.response;
              const needed = response?.needed || 'channels:join or channels:write';
              const provided = response?.provided || 'unknown';
              throw new Error(`Missing OAuth permissions. Need: ${needed}. Currently have: ${provided}. Please update your Slack app's OAuth scopes and reinstall.`);
            }
            if (joinError.errorCode === 'cant_invite_self' || joinError.message?.includes('cant_invite_self')) {
              throw new Error(`Bot cannot join this channel automatically. Please manually invite the bot to the channel in Slack first.`);
            }
            if (joinError.errorCode === 'channel_not_found') {
              throw new Error(`Channel not found. Please check the channel exists and the bot has access.`);
            }
            throw new Error(`Cannot send message: ${joinError.message || 'Bot needs to be invited to this channel first.'}`);
          }
        }

        // If original error was not_in_channel and we couldn't auto-join, provide manual instructions
        if (error.errorCode === 'not_in_channel' || error.message?.includes('not_in_channel')) {
          throw new Error(`Bot is not in this channel. Please manually invite the bot to the channel in Slack, then try sending the message again.`);
        }

        // Re-throw any other errors
        throw error;
      }
    },
    onSuccess: (data, variables) => {
      console.log('Message sent successfully:', data);

      // Generate stable mutation ID based on message content
      const mutationId = `success-${variables.channelId}-${variables.text.slice(0, 20)}`;

      if (currentMutationId === mutationId) {
        console.log('Duplicate success call detected, skipping history update');
        return;
      }
      setCurrentMutationId(mutationId);

      // Add to message history
      const channelName = (channels as SlackChannel[]).find(c => c.id === variables.channelId)?.name || variables.channelId;
      const historyEntry = {
        id: data.ts,
        channelId: variables.channelId,
        channelName,
        text: variables.text,
        timestamp: new Date().toISOString(),
        success: true,
        type: 'sent' as const,
        user: 'Bot'
      };

      setMessageHistory(prev => [historyEntry, ...prev]);

      setMessage('');
      onMessageSent?.(variables.channelId, variables.text);
      queryClient.invalidateQueries({ queryKey: ['slack', 'messages', variables.channelId] });

      // Clear mutation ID after a delay
      setTimeout(() => setCurrentMutationId(null), 1000);
    },
    onError: (error: any, variables) => {
      console.error('Send message mutation error:', error);

      // Generate stable mutation ID based on message content
      const mutationId = `error-${variables.channelId}-${variables.text.slice(0, 20)}`;

      if (currentMutationId === mutationId) {
        console.log('Duplicate error call detected, skipping history update');
        return;
      }
      setCurrentMutationId(mutationId);

      // Only add failed message to history for truly final failures
      const channelName = (channels as SlackChannel[]).find(c => c.id === variables.channelId)?.name || variables.channelId;
      const historyEntry = {
        id: `failed-${Date.now()}`,
        channelId: variables.channelId,
        channelName,
        text: variables.text,
        timestamp: new Date().toISOString(),
        success: false,
        type: 'sent' as const,
        user: 'Bot'
      };

      setMessageHistory(prev => [historyEntry, ...prev]);

      onError?.(error.message || 'Failed to send message');

      // Clear mutation ID after a delay
      setTimeout(() => setCurrentMutationId(null), 1000);
    },
  });

  const handleSaveConfig = () => {
    try {
      localStorage.setItem('slack-config', JSON.stringify(config));
      if (config.botToken) {
        setupSlackTokens(config.botToken, config.userToken);
        setIsConfigured(true);
        setShowConfig(false);
        refetchChannels();
      }
    } catch (error) {
      onError?.('Failed to save configuration');
    }
  };

  const handleSendMessage = () => {
    if (!selectedChannelId || !message.trim()) {
      onError?.('Please select a channel and enter a message');
      return;
    }

    sendMessageMutation.mutate({
      channelId: selectedChannelId,
      text: message.trim(),
    });
  };

  const getChannelIcon = (channel: SlackChannel) => {
    if (channel.is_private) return <Lock className="w-4 h-4 text-red-500" />;
    if (channel.is_im || channel.is_mpim) return <Users className="w-4 h-4 text-blue-500" />;
    return <Hash className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
  };

  const getChannelTypeLabel = (channel: SlackChannel) => {
    if (channel.is_private) return 'Private';
    if (channel.is_im) return 'Direct Message';
    if (channel.is_mpim) return 'Group Message';
    return 'Public';
  };

  const selectedChannel = (channels as SlackChannel[]).find(c => c.id === selectedChannelId);

  if (!isConfigured) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Slack Integration</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Connect your Slack workspace</p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-amber-900 dark:text-amber-300">Quick Start (Manual Setup)</h3>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  To test immediately: Go to your Slack channel and type <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">/invite @your-bot-name</code> to invite the bot, then send messages normally.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">Automatic Setup (OAuth Scopes)</h3>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                  To use Slack integration, you need to provide your bot token. You can get this from your Slack app settings.
                </p>
                <div className="mt-3">
                  <p className="text-xs font-medium text-blue-900 dark:text-blue-300 mb-1">Required OAuth Scopes:</p>
                  <div className="space-y-1">
                    <div>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mb-0.5">Bot Token Scopes (for full functionality):</p>
                      <div className="flex flex-wrap gap-1">
                        <span className="inline-block px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs rounded">channels:read</span>
                        <span className="inline-block px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs rounded">channels:join</span>
                        <span className="inline-block px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs rounded">chat:write</span>
                        <span className="inline-block px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs rounded">users:read</span>
                        <span className="inline-block px-2 py-0.5 bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200 text-xs rounded">channels:history</span>
                        <span className="inline-block px-2 py-0.5 bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200 text-xs rounded">groups:history</span>
                        <span className="inline-block px-2 py-0.5 bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200 text-xs rounded">mpim:history</span>
                        <span className="inline-block px-2 py-0.5 bg-orange-100 dark:bg-orange-800 text-orange-800 dark:text-orange-200 text-xs rounded">im:history</span>
                      </div>
                      <p className="text-xs text-orange-700 dark:text-orange-400 mt-1">
                        <strong>History scopes</strong> are required to receive messages from all channel types
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mb-0.5">User Token Scopes (alternative):</p>
                      <div className="flex flex-wrap gap-1">
                        <span className="inline-block px-2 py-0.5 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 text-xs rounded">channels:write</span>
                        <span className="inline-block px-2 py-0.5 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 text-xs rounded">chat:write</span>
                        <span className="inline-block px-2 py-0.5 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 text-xs rounded">channels:history</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowConfig(true)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <Settings className="w-5 h-5" />
            <span>Configure Slack</span>
          </button>

          {showConfig && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Slack Configuration</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bot Token <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={config.botToken}
                      onChange={(e) => setConfig({ ...config, botToken: e.target.value })}
                      placeholder="xoxb-your-bot-token"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      User Token (Optional)
                    </label>
                    <input
                      type="password"
                      value={config.userToken || ''}
                      onChange={(e) => setConfig({ ...config, userToken: e.target.value })}
                      placeholder="xoxp-your-user-token"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Webhook URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={config.webhookUrl || ''}
                      onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
                      placeholder="https://hooks.slack.com/..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={() => setShowConfig(false)}
                    className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white py-2 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveConfig}
                    disabled={!config.botToken}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition-colors"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Slack Integration</h2>
              <div className="flex items-center space-x-2">
                {connectionTest && !connectionError ? (
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600 dark:text-green-400">
                      Connected to {connectionTest.team}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-red-600 dark:text-red-400">Connection error</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => refetchChannels()}
              disabled={channelsLoading}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${channelsLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowConfig(true)}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Channel Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Channel
          </label>
          {channelsLoading ? (
            <div className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
              <div className="animate-pulse flex items-center space-x-2">
                <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded flex-1"></div>
              </div>
            </div>
          ) : channelsError ? (
            <div className="w-full p-3 border border-red-300 dark:border-red-600 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              Failed to load channels. Please check your token and try again.
            </div>
          ) : (
            <select
              value={selectedChannelId}
              onChange={(e) => setSelectedChannelId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Choose a channel...</option>
              {(channels as SlackChannel[]).map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.is_private ? '🔒' : '#'} {channel.name}
                  {channel.num_members ? ` (${channel.num_members} members)` : ''}
                  {channel.is_archived ? ' (archived)' : ''}
                </option>
              ))}
            </select>
          )}

          {selectedChannel && (
            <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-2 text-sm">
                {getChannelIcon(selectedChannel)}
                <span className="font-medium text-gray-900 dark:text-white">
                  #{selectedChannel.name}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  • {getChannelTypeLabel(selectedChannel)}
                </span>
                {selectedChannel.num_members && (
                  <span className="text-gray-500 dark:text-gray-400">
                    • {selectedChannel.num_members} members
                  </span>
                )}
              </div>
              {selectedChannel.purpose.value && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {selectedChannel.purpose.value}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Slack-style Message History */}
        {(messageHistory.length > 0 || selectedChannelId) && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
                <Hash className="w-4 h-4" />
                <span>
                  {selectedChannelId
                    ? `#${(channels as SlackChannel[]).find(c => c.id === selectedChannelId)?.name || 'Channel'}`
                    : 'Message History'
                  }
                </span>
                {messagesLoading && <RefreshCw className="w-3 h-3 animate-spin text-gray-400" />}
                {newMessageCount > 0 && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 animate-pulse">
                    +{newMessageCount} new
                  </span>
                )}
              </h3>
              <div className="flex items-center space-x-2">
                {selectedChannelId && !messagesError && (
                  <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                    <div className={`w-2 h-2 rounded-full ${
                      webhookConnected
                        ? 'bg-green-500 animate-pulse'
                        : usePolling
                        ? 'bg-blue-500 animate-pulse'
                        : 'bg-yellow-500'
                    }`}></div>
                    <span>
                      {webhookConnected ? 'Live' : usePolling ? 'Polling' : 'Connecting...'}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => refetchMessages()}
                  disabled={!selectedChannelId || messagesLoading}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3 h-3 ${messagesLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setMessageHistory([])}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Slack-style conversation view */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg max-h-80 overflow-y-auto">
              <div className="p-4 space-y-4">
                {messagesError ? (
                  <div className="text-center text-orange-600 dark:text-orange-400 py-8">
                    <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">Cannot receive messages</p>
                    <p className="text-xs mt-1">Missing history scopes in your bot token</p>
                    <p className="text-xs mt-1">Add these scopes: channels:history, groups:history, mpim:history, im:history</p>
                    <p className="text-xs mt-1">Then reinstall the app to your workspace</p>
                  </div>
                ) : messageHistory.length === 0 && selectedChannelId && !messagesLoading ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No messages yet in this channel.</p>
                    <p className="text-xs mt-1">Send a message or wait for new activity!</p>
                  </div>
                ) : (
                  messageHistory.slice().reverse().map((msg) => (
                    <div key={msg.id} className="flex items-start space-x-3">
                      {/* User Avatar */}
                      <div className="flex-shrink-0">
                        {msg.type === 'sent' ? (
                          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <span className="text-white text-xs font-bold">B</span>
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">
                              {(msg.user || 'U')[0].toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Message Content */}
                      <div className="flex-1 min-w-0">
                        {/* Header with name, channel, and timestamp */}
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">
                            {msg.user || 'Unknown User'}
                          </span>
                          {msg.type === 'sent' && (
                            <>
                              <span className="text-xs text-gray-500 dark:text-gray-400">to</span>
                              <div className="flex items-center space-x-1">
                                <Hash className="w-3 h-3 text-gray-400" />
                                <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                                  {msg.channelName}
                                </span>
                              </div>
                            </>
                          )}
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(msg.timestamp).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                          {/* Status indicator for sent messages */}
                          {msg.type === 'sent' && (
                            msg.success ? (
                              <CheckCircle className="w-3 h-3 text-green-500" />
                            ) : (
                              <AlertCircle className="w-3 h-3 text-red-500" />
                            )
                          )}
                        </div>

                        {/* Message bubble */}
                        <div
                          className={`inline-block px-3 py-2 rounded-lg max-w-full break-words text-sm ${
                            msg.type === 'received'
                              ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                              : msg.success
                              ? 'bg-blue-500 text-white'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700'
                          }`}
                        >
                          {msg.text}
                        </div>

                        {/* Error message for failed sends */}
                        {msg.type === 'sent' && !msg.success && (
                          <div className="mt-1 text-xs text-red-600 dark:text-red-400 italic">
                            Failed to send - bot not in channel
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Message Composer */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white resize-none"
            />
          </div>

          <button
            onClick={handleSendMessage}
            disabled={!selectedChannelId || !message.trim() || sendMessageMutation.isPending}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            {sendMessageMutation.isPending ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
            <span>
              {sendMessageMutation.isPending ? 'Sending...' : 'Send Message'}
            </span>
          </button>

          {sendMessageMutation.isSuccess && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-700 dark:text-green-400">
                  Message sent successfully!
                </span>
              </div>
            </div>
          )}

        </div>

        {/* Configuration Modal */}
        {showConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Update Slack Configuration</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bot Token <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={config.botToken}
                    onChange={(e) => setConfig({ ...config, botToken: e.target.value })}
                    placeholder="xoxb-your-bot-token"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    User Token (Optional)
                  </label>
                  <input
                    type="password"
                    value={config.userToken || ''}
                    onChange={(e) => setConfig({ ...config, userToken: e.target.value })}
                    placeholder="xoxp-your-user-token"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Webhook URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={config.webhookUrl || ''}
                    onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
                    placeholder="https://hooks.slack.com/..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowConfig(false)}
                  className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white py-2 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveConfig}
                  disabled={!config.botToken}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};